import { ApplicationElement, html } from '@effectivastudio/lit-components';
import { ifDefined } from 'lit/directives/if-defined.js';
import '../date_widget/element.js';
import '../origin/element.js';
import style from './style.lit.css';
import translations from './translations.js';

/**
 * Event card element - links to a card with more details. Event name/text is inserted via default slot.
 * @prop startDate - For card's xs-date-widget and date bellow title.
 * @prop endDate - For calculating finalDate. Final date is displayed date if current date is between startDate and endDate.
 * @prop url - Cards's destination url.
 * @prop label - Card's label in right-bottom corner.
 * @prop region - region name for the origin component.
 * @prop regionImage - region image for the origin component.
 * @prop regionIcon - region icon for the origin component if region image is not defined.
 *
 * @slot default - For title.
 *
 * @cssprop --height - Component height.
 * @cssprop --date-widget-bg - Date widget background.
 * @cssprop --date-widget-text - Date widget text color.
 * @cssprop --bg - Component background.
 * @cssprop --bg-hover - Component background on hover.
 * @cssprop --bg-active - Component background on clicked.
 * @cssprop --color - Component text color.
 */

export default class Card_V2 extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    startDate: { type: Date },
    endDate: { type: Date },
    url: { type: String },
    region: { type: String },
    regionImage: { type: String },
    regionIcon: { type: String },
    label: { type: String },
  };

  /** @internal */
  static translations = translations;

  renderLabel() {
    return html`<ef-label layout="md">${this.label}</ef-label>`;
  }

  /** @internal */
  get dateString() {
    const formatted = this.i18n.strftime(this.startDate, '%d.%m.%Y');

    return html`<span>${formatted}</span>`;
  }

  /** @internal */
  get timeString() {
    const time = this.i18n.l('date.formats.time', this.startDate);

    return html`<span>${time} ${this.t('hours')}</span>`;
  }

  /** @internal */
  get shouldDisplayCurrentDate() {
    const currentDate = this.resetDateTiming(new Date());

    return (
      currentDate > this.resetDateTiming(this.startDate) &&
      currentDate <= this.resetDateTiming(this.endDate)
    );
  }

  /** @internal */
  get finalDate() {
    return this.shouldDisplayCurrentDate ? new Date() : this.startDate;
  }

  resetDateTiming(date) {
    const tempDate = new Date(date);

    tempDate.setHours(0);
    tempDate.setMinutes(0);
    tempDate.setSeconds(0);
    tempDate.setMilliseconds(0);

    return tempDate;
  }

  render() {
    return html`<a href=${ifDefined(this.url)}>
      <xs-card-date-widget .date=${this.finalDate}></xs-card-date-widget>
      <section>
        <div class="main-content">
          <slot></slot>
          <time datetime=${this.startDate?.toISOString()}>
            ${this.dateString} ${this.timeString}
          </time>
        </div>
        <footer>
          <xs-origin
            .name=${this.region}
            .picture=${this.regionImage}
            .icon=${this.regionIcon}>
            ${this.region}
          </xs-origin>
          ${this.label ? this.renderLabel() : null}
        </footer>
      </section>
    </a>`;
  }
}

Card_V2.registerTagName('xs-card-v2');

customElements.get('xs-card-v2') ||
  customElements.define('xs-card-v2', Card_V2);
